import { useState } from 'react'

export type AsyncEffectHandle<F extends (...args: any[]) => any = () => Promise<any>> = [
    // The loading state of the effect.
    boolean,
    // An error if one arose during the effect.
    Error | undefined,
    // The repeat function to force the effect to run again.
    F,
]

export type LoadingSignal = { loadingSignal: boolean }

/**
 * Generate an async effect function with a loading state and error value.
 * The handle is not an effect, but rather provides a wrapper around code that could run as an effect.
 * The purpose of this approach is the handle an effect with reusable loading and error state code,
 *  while also exposing the given handle to be called arbitrarily ex post facto.
 *
 * NOTE: Due to generic semantics, this function should not be used outside tooling.
 */
export const useAsyncEffectGenerator = <A extends [], R>(
    effect: (...args: A) => R | Promise<R>,
    initialLoadingState: boolean = false
): AsyncEffectHandle<(...args: A) => Promise<R | undefined>> => {
    const [loading, setLoading] = useState(initialLoadingState)
    const [error, setError] = useState<Error>()

    // Reset the error state after a short while to avoid it being output multiple times when navigating.
    // The error should be shown immediately and not reused.
    // The timeout only serves to batch a render that includes the error.
    // Ideally the timeout is as short as possible, but longer times are better at avoiding race conditions.
    const resetErrorDelayed = () => setTimeout(() => setError(undefined), 500)

    // Represent the loading signal given by an effect or,
    //  revert the state if the effect did not return a signal.
    const revertLoading = (signal?: LoadingSignal) => setLoading(signal?.loadingSignal ?? false)

    const handle = async (...args: A) => {
        setLoading(true)
        let r: R | undefined = undefined
        try {
            r = await effect(...args)
            // Do not unset the error before the request succeeds.
            // Otherwise, it may fluctuate between an error and undefined and back again, triggering states unnecessarily.
            setError(undefined)
        } catch (e) {
            // Errors are only parsed here, not handled!
            const error = parseError(e)
            setError(error)
            resetErrorDelayed()
        }

        // Always revert loading state if the effect did not return a loading signal.
        revertLoading(r as LoadingSignal)

        return r
    }

    return [loading, error, handle]
}

/**
 * Ensure only Error types are forwarded when thrown by converting everything else into a standard Error.
 */
const parseError = (error: unknown): Error => {
    // No error = unknown error
    if (!error) return new Error('Unknown error')
    // Error is already an error
    else if (error instanceof Error) return error
    // Error is a string, so assume it is the message.
    else if (typeof error === 'string') return new Error(error)
    // Error is something else entirely, but not nothing, so we try to stringify it.
    else return new Error(JSON.stringify(error, null, 4))
}
